/**
 * Single photo thumbnail with status badges. Shows "needs scale" when
 * the estimate has zero anchors (FR-D5).
 */

import { useState } from "react";

import { Lightbox } from "@/components/ui/Lightbox";
import { deletePhoto } from "@starz/shared/api/sdk.gen";
import type { Photo } from "@starz/shared/api/types.gen";

interface Props {
  photo: Photo;
  estimateId: string;
  /** True when no anchor has been recorded for the estimate yet. */
  needsScale: boolean;
  /** Hide the delete control (admin view). */
  readOnly?: boolean;
  onDeleted: (photoId: string) => void;
}

const STATUS_BADGE: Record<Photo["dimension_status"], { label: string; className: string }> = {
  pending: { label: "Awaiting scale", className: "bg-starz-cream text-starz-charcoal" },
  processing: { label: "Measuring…", className: "bg-amber-100 text-amber-900" },
  done: { label: "Measured", className: "bg-emerald-100 text-emerald-900" },
  failed: { label: "Couldn't measure", className: "bg-rose-100 text-rose-900" },
};

export function PhotoCard({ photo, estimateId, needsScale, readOnly = false, onDeleted }: Props) {
  const [removing, setRemoving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  async function onDelete() {
    if (!window.confirm("Remove this photo?")) return;
    setRemoving(true);
    const result = await deletePhoto({ estimateId, photoId: photo.photo_id });
    setRemoving(false);
    if ("error" in result) {
      window.alert(`Delete failed: ${result.error.error.message}`);
      return;
    }
    onDeleted(photo.photo_id);
  }

  const isRoomScan = photo.category === "room_scan";
  const badge = isRoomScan
    ? { label: "3D scan", className: "bg-blue-100 text-blue-900" }
    : needsScale && photo.dimension_status === "pending"
      ? { label: "Needs scale", className: "bg-amber-100 text-amber-900" }
      : STATUS_BADGE[photo.dimension_status];

  const previewSrc = photo.thumbnail_url ?? photo.original_url ?? null;

  const fullSrc = photo.original_url ?? previewSrc;

  return (
    <figure className="group relative overflow-hidden rounded-xl border border-starz-gold/15 bg-starz-cream/30">
      {previewSrc ? (
        <button
          type="button"
          onClick={() => fullSrc && setLightboxOpen(true)}
          className="block w-full"
          aria-label="View photo full screen"
        >
          <img
            src={previewSrc}
            alt={isRoomScan ? "3D room scan" : "Customer photo"}
            loading="lazy"
            className="aspect-square w-full object-cover transition-opacity hover:opacity-90"
          />
        </button>
      ) : (
        <div className="grid aspect-square w-full place-items-center text-xs text-warm-gray">
          {photo.upload_status === "presigned" ? "Uploading…" : "No preview"}
        </div>
      )}
      <Lightbox
        open={lightboxOpen}
        src={fullSrc ?? null}
        alt="Customer photo"
        onClose={() => setLightboxOpen(false)}
      />
      <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 font-medium ${badge.className}`}>
          {badge.label}
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={onDelete}
            disabled={removing}
            aria-label="Remove this photo"
            className="text-warm-gray hover:text-rose-600 disabled:opacity-40"
          >
            {removing ? "…" : "✕"}
          </button>
        )}
      </figcaption>
    </figure>
  );
}
